import { apiGet, buildApiUrl } from './client';

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  userId?: string | null;
  organizationId?: string | null;
  action: string;
  resource: string;
  resourceId?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  metadata?: Record<string, unknown> | null;
  /** Resolved actor identity (null for anonymous or deleted users). */
  actor?: { name: string | null; email: string } | null;
}

export type AuditCategory = 'auth' | 'oauth' | 'access' | 'admin';
export type AuditCategoryCounts = Record<AuditCategory | 'all', number>;
export interface AuditActor {
  id: string;
  name: string | null;
  email: string;
}

export interface AuditQuery {
  action?: string;
  resource?: string;
  userId?: string;
  resourceId?: string;
  startDate?: string;
  endDate?: string;
  category?: AuditCategory;
  q?: string;
  limit?: number;
  offset?: number;
}

export const auditApi = {
  list: (query: AuditQuery = {}): Promise<AuditLogEntry[]> => {
    const params: Record<string, string | number | undefined> = {
      action: query.action || undefined,
      resource: query.resource || undefined,
      userId: query.userId || undefined,
      resourceId: query.resourceId || undefined,
      startDate: query.startDate || undefined,
      endDate: query.endDate || undefined,
      category: query.category || undefined,
      q: query.q || undefined,
      limit: query.limit,
      offset: query.offset,
    };
    return apiGet<AuditLogEntry[]>('/api/audit', { params });
  },

  /** List with total count (offset-based pagination). */
  listWithTotal: async (
    query: AuditQuery = {}
  ): Promise<{ data: AuditLogEntry[]; total: number; hasMore: boolean }> => {
    const params: Record<string, string | number | undefined> = {
      action: query.action || undefined,
      resource: query.resource || undefined,
      userId: query.userId || undefined,
      resourceId: query.resourceId || undefined,
      startDate: query.startDate || undefined,
      endDate: query.endDate || undefined,
      category: query.category || undefined,
      q: query.q || undefined,
      limit: query.limit,
      offset: query.offset,
    };
    const envelope = await apiGetPaginatedRaw(params);
    return envelope;
  },

  summary: (): Promise<AuditCategoryCounts> => apiGet<AuditCategoryCounts>('/api/audit/summary'),

  actors: (): Promise<AuditActor[]> => apiGet<AuditActor[]>('/api/audit/actors'),

  get: (id: string): Promise<AuditLogEntry> => apiGet<AuditLogEntry>(`/api/audit/${id}`),
};

// The audit endpoint reports { pagination: { total, hasMore } }, which differs from PaginationInfo.
async function apiGetPaginatedRaw(
  params: Record<string, string | number | undefined>
): Promise<{ data: AuditLogEntry[]; total: number; hasMore: boolean }> {
  const response = await fetch(buildApiUrl('/api/audit', params), { credentials: 'include' });
  const body = (await response.json()) as {
    success: boolean;
    data?: AuditLogEntry[];
    pagination?: { total: number; hasMore: boolean };
    error?: { message?: string } | string;
  };
  if (!response.ok || !body.success) {
    const msg = typeof body.error === 'string' ? body.error : body.error?.message;
    throw new Error(msg || 'Failed to load audit log');
  }
  return {
    data: body.data ?? [],
    total: body.pagination?.total ?? body.data?.length ?? 0,
    hasMore: body.pagination?.hasMore ?? false,
  };
}
